import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { NewInvitationContent } from '../components/new-invitation/NewInvitationContent';
import {
  useAcceptInvitationPhoenix,
  useGetNursingHomeListPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface NewInvitationPageProps {}

export const NewInvitationPage: FC<NewInvitationPageProps> = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant');
  const nursingHomeId = searchParams.get('nursingHomeId');
  const globalServiceProviderId = searchParams.get('globalServiceProviderId');

  const { mutateAsync: acceptInvitationPhoenix } = useAcceptInvitationPhoenix();

  const { data: nursingHomeList } = useGetNursingHomeListPhoenix();

  if (!tenant || !globalServiceProviderId || !nursingHomeId) {
    throw new Error('Missing required parameters for invitation');
  }

  // The nursing home is not there if a) the nursing home does not exist or b) the invitation is not valid anymore
  const nursingHome = useMemo(() => {
    return nursingHomeList?.find(
      (nursingHome) => nursingHome.nursing_home_id === nursingHomeId
    );
  }, [nursingHomeList, nursingHomeId]);

  const onAcceptInvitation = useCallback(async () => {
    // If the nursing home is not there, it either does not exist (unlikely) or the
    // invitation is not valid anymore; in that case we just navigate home.
    if (nursingHome) {
      await acceptInvitationPhoenix({
        nursing_home_id: nursingHomeId as string,
      });
      enqueueSnackbar(
        t('service-provider-app.new-invitation.invitation-accepted'),
        { variant: 'success' }
      );
    }
    navigate(spAppMainPages.HOME);
  }, [
    nursingHome,
    navigate,
    enqueueSnackbar,
    t,
    acceptInvitationPhoenix,
    nursingHomeId,
  ]);

  return (
    <NewInvitationContent
      accountAffiliationName={nursingHome?.nursing_home_name}
      connectionStatus={nursingHome?.connection_status}
      onAcceptInvitation={onAcceptInvitation}
    />
  );
};
